import { useEffect, useState } from "react";
import { api, fmtNum, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Upload as UploadIcon,
  FileSpreadsheet,
  CheckCircle2,
  Lock,
  Eye,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const COLL_LABELS = {
  policies: "Policies",
  broker_accounts: "Broker Accounts",
  master_policy: "Master Policy",
  siriuspoint_policies: "SiriusPoint",
  tax_rates: "Tax Rates",
  commission_notes: "Commission Notes",
};

export default function Upload() {
  const { user } = useAuth();
  const canWrite = user?.role === "admin" || user?.role === "controller";

  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null); // {preview: {coll: {total, new, existing}}, filename}
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    refreshHistory();
  }, []);

  function refreshHistory() {
    api.get("/upload/history").then((r) => setHistory(r.data)).catch(() => {});
  }

  function resetAll() {
    setFile(null);
    setPreview(null);
    setResult(null);
  }

  async function runPreview(e) {
    e?.preventDefault?.();
    if (!file) return;
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload?dry_run=true", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setPreviewing(false);
    }
  }

  async function commit() {
    if (!file) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      setResult(data.summary);
      setPreview(null);
      toast.success("Upload complete");
      refreshHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setCommitting(false);
    }
  }

  return (
    <div className="space-y-6 max-w-[1100px]" data-testid="upload-page">
      <Card className="p-8 border-slate-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center ring-1 ring-blue-100">
            <UploadIcon className="w-6 h-6 text-blue-600" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold tracking-tight">Upload monthly MGA file</h2>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">
              Drop your latest <code className="text-slate-700 bg-slate-100 px-1 rounded text-[12px]">.xlsx</code> export.
              {canWrite
                ? " Preview the impact first, then commit. New rows are appended; identical rows are skipped."
                : " Uploads are read-only for your role — past uploads are visible below."}
            </p>

            {!canWrite && (
              <div
                data-testid="upload-readonly-notice"
                className="mt-5 flex items-start gap-3 rounded-lg bg-slate-50 border border-slate-200 p-4"
              >
                <Lock className="w-4 h-4 text-slate-400 mt-0.5" strokeWidth={2} />
                <div className="text-[12px] text-slate-600 leading-relaxed">
                  Your role (<b className="text-slate-800">Viewer</b>) can browse uploads but cannot ingest new
                  workbooks. Ask an admin or controller to upload new data, or request an access change.
                </div>
              </div>
            )}

            {canWrite && (
              <form onSubmit={runPreview} className="mt-6 space-y-4">
                <label
                  htmlFor="file-input"
                  data-testid="upload-dropzone"
                  className="block border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
                >
                  <FileSpreadsheet className="w-8 h-8 mx-auto text-slate-400" />
                  <div className="mt-3 text-sm font-medium text-slate-700">
                    {file ? file.name : "Click to select a workbook"}
                  </div>
                  <div className="text-[12px] text-slate-400 mt-1">.xlsx up to 50 MB</div>
                  <input
                    id="file-input"
                    type="file"
                    accept=".xlsx,.xls"
                    data-testid="upload-file-input"
                    className="hidden"
                    onChange={(e) => {
                      setFile(e.target.files?.[0] || null);
                      setPreview(null);
                      setResult(null);
                    }}
                  />
                </label>

                <div className="flex gap-2">
                  {!preview && (
                    <Button
                      type="submit"
                      disabled={!file || previewing}
                      data-testid="upload-preview-button"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Eye className="w-4 h-4 mr-1.5" />
                      {previewing ? "Analysing…" : "Preview impact"}
                    </Button>
                  )}
                  {file && (
                    <Button type="button" variant="outline" onClick={resetAll}>
                      Reset
                    </Button>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </Card>

      {preview && (
        <PreviewPanel
          preview={preview}
          committing={committing}
          onCommit={commit}
          onCancel={() => setPreview(null)}
          onRerun={runPreview}
        />
      )}

      {result && (
        <Card className="p-6 border-slate-200" data-testid="upload-result">
          <div className="flex items-center gap-2 mb-4 text-emerald-600">
            <CheckCircle2 className="w-5 h-5" />
            <h3 className="font-semibold">Ingestion complete</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.entries(result).map(([coll, r]) => (
              <div key={coll} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="text-[11px] uppercase tracking-wider text-slate-400">
                  {COLL_LABELS[coll] || coll}
                </div>
                <div className="text-lg font-semibold tracking-tight mt-1">{fmtNum(r.total)} rows</div>
                <div className="text-[12px] text-slate-500 mt-1">
                  +{fmtNum(r.inserted)} new • {fmtNum(r.updated)} modified
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold tracking-tight">Recent uploads</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="px-6 py-2 font-medium">When</th>
                <th className="px-6 py-2 font-medium">File</th>
                <th className="px-6 py-2 font-medium">By</th>
                <th className="px-6 py-2 font-medium">Policies +/upd</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-slate-400 text-sm">
                    No uploads yet.
                  </td>
                </tr>
              )}
              {history.map((h, i) => {
                const p = h.summary?.policies || {};
                return (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="px-6 py-3 text-slate-600">{fmtDate(h.uploaded_at)}</td>
                    <td className="px-6 py-3 text-slate-900 font-medium">{h.filename}</td>
                    <td className="px-6 py-3 text-slate-600">{h.uploaded_by}</td>
                    <td className="px-6 py-3 text-slate-600">
                      +{fmtNum(p.inserted || 0)} / {fmtNum(p.updated || 0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function PreviewPanel({ preview, committing, onCommit, onCancel, onRerun }) {
  const rows = Object.entries(preview.preview || {});
  const totals = rows.reduce(
    (acc, [, v]) => ({
      total: acc.total + (v.total || 0),
      new: acc.new + (v.new || 0),
      existing: acc.existing + (v.existing || 0),
    }),
    { total: 0, new: 0, existing: 0 },
  );
  const noChanges = totals.new === 0 && totals.total > 0;

  return (
    <Card className="border-slate-200" data-testid="upload-preview">
      <div className="p-6 border-b border-slate-100">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-blue-600">
              <Eye className="w-4 h-4" />
              <h3 className="font-semibold text-slate-900">Impact preview</h3>
              <span className="text-[11px] uppercase tracking-wider text-blue-500 bg-blue-50 ring-1 ring-blue-100 px-1.5 py-0.5 rounded">
                Dry run
              </span>
            </div>
            <p className="text-sm text-slate-500 mt-1.5 max-w-xl">
              Parsed <b className="text-slate-800">{preview.filename}</b>. Nothing has been written yet — review the
              counts below, then commit when you're happy.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-5">
          <SummaryTile label="Rows parsed" value={totals.total} tone="slate" />
          <SummaryTile label="New to DB" value={totals.new} tone="emerald" testid="preview-total-new" />
          <SummaryTile label="Already present" value={totals.existing} tone="slate" testid="preview-total-existing" />
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mb-2">
          Per tab
        </div>
        <div className="divide-y divide-slate-100">
          {rows.map(([coll, v]) => {
            const share = v.total ? Math.round((v.new / v.total) * 100) : 0;
            return (
              <div
                key={coll}
                data-testid={`preview-row-${coll}`}
                className="py-3 grid grid-cols-[1.5fr_90px_90px_90px_140px] items-center gap-4"
              >
                <div className="text-sm text-slate-700 font-medium truncate">
                  {COLL_LABELS[coll] || coll}
                </div>
                <div className="text-right text-sm tabular-nums text-slate-900">
                  {fmtNum(v.total)}
                </div>
                <div className="text-right text-sm tabular-nums text-emerald-700 font-medium">
                  +{fmtNum(v.new)}
                </div>
                <div className="text-right text-sm tabular-nums text-slate-500">
                  {fmtNum(v.existing)}
                </div>
                <div className="pl-2">
                  <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full bg-emerald-500"
                      style={{ width: `${Math.min(100, share)}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1 tabular-nums text-right">
                    {share}% new
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/70 flex items-center justify-between flex-wrap gap-3">
        <div className="text-[12px] text-slate-500">
          {noChanges
            ? "All rows already exist in the database — committing will be a no-op."
            : `Committing will insert ${fmtNum(totals.new)} new row${totals.new === 1 ? "" : "s"} across ${rows.length} tab${rows.length === 1 ? "" : "s"}.`}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onCancel} data-testid="preview-cancel">
            Cancel
          </Button>
          <Button variant="outline" onClick={onRerun} data-testid="preview-rerun">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-run preview
          </Button>
          <Button
            onClick={onCommit}
            disabled={committing}
            data-testid="preview-commit"
            className="bg-blue-600 hover:bg-blue-700"
          >
            {committing ? "Committing…" : "Commit upload"}
            {!committing && <ArrowRight className="w-4 h-4 ml-1.5" />}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function SummaryTile({ label, value, tone, testid }) {
  const toneMap = {
    slate: "bg-slate-50 text-slate-900 ring-slate-200",
    emerald: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-lg p-4 ring-1 ${toneMap[tone] || toneMap.slate}`}
    >
      <div className="text-[11px] uppercase tracking-wider opacity-70 font-medium">{label}</div>
      <div className="text-2xl font-semibold tracking-tight tabular-nums mt-1">
        {fmtNum(value)}
      </div>
    </div>
  );
}
